import { useState } from 'react'
import { useDrop, useDrag } from '@sketch/modules-common'
import {
  StorageItemFragment,
  useGetDocumentListSettingsQuery,
} from '@sketch/gql-types'
import { useSelectionContext } from '../../storageItems/components/SelectionContext'
import { createDnDGhost } from '../../storageItems/utils/createDnDGhost'

interface UseShareListItemDragDropProps {
  disabled: boolean
  shareId: string
  onStorageItemsDropped?: (relatedItems: StorageItemFragment[]) => void
}

export const useShareListItemDragDrop = ({
  disabled,
  shareId: droppedShareId,
  onStorageItemsDropped,
}: UseShareListItemDragDropProps) => {
  const { data } = useGetDocumentListSettingsQuery()

  const { selectedItems } = useSelectionContext()
  const [isDroppableAreaEnabled, setIsDroppableAreaEnabled] = useState(false)

  const dropEvents = useDrop({
    onStorageItemsDragOver: (draggedItem, relatedItems) => {
      if (relatedItems.find(item => item.identifier === droppedShareId)) {
        // If share was dropped on itself, ignore
        return
      }

      setIsDroppableAreaEnabled(true)
    },
    onStorageItemsDropped: (_, relatedItems) => {
      if (relatedItems.find(item => item.identifier === droppedShareId)) {
        // If share was dropped on itself, ignore
        return
      }

      onStorageItemsDropped?.(relatedItems)
    },
    onUnknownDragOver: () => {
      setIsDroppableAreaEnabled(false)
    },
  })

  const dragEvents = useDrag({
    item: { __typename: 'Share', identifier: droppedShareId },
    relatedItems: selectedItems,
    createCustomElement: el =>
      createDnDGhost(el, data?.documentsLayout === 'LIST' ? 'list' : 'grid'),
    customDragImage: true,
  })

  return {
    draggable: !disabled,
    ...dragEvents,
    ...dropEvents,
    isDroppableAreaEnabled,
  }
}
